import { useCallback, useEffect, useMemo, useRef } from "react"

type DebouncedFunction<T extends (...args: never[]) => void> = T & { cancel: () => void }

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useDebounce<T extends (...args: any[]) => void>(callback: T, delay: number): DebouncedFunction<T> {
  const callbackRef = useRef(callback)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  const cancel = useCallback(() => {
    if (!timeoutRef.current) return

    clearTimeout(timeoutRef.current)
    timeoutRef.current = undefined
  }, [])

  return useMemo(() => {
    const debounced = ((...args: Parameters<T>) => {
      cancel()

      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = undefined
        callbackRef.current(...args)
      }, delay)
    }) as DebouncedFunction<T>

    debounced.cancel = cancel
    return debounced
  }, [cancel, delay])
}
